import { useState, useEffect } from 'react';
import { AudioPlayer } from './components/AudioPlayer';
import { getDeviceUUID } from './components/SignalRating';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';

function daysUntil(dateStr) {
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function ScoreRing({ score }) {
  const pct = score / 100;
  const r = 36, circ = 2 * Math.PI * r;
  const dash = pct * circ;
  const color = score >= 75 ? '#27AE60' : score >= 55 ? '#D4A574' : score >= 40 ? '#F39C12' : '#34495E';
  const label = score >= 75 ? 'Expert' : score >= 60 ? 'Senior' : score >= 45 ? 'Analyst' : 'Junior';

  return (
    <div className="score-ring-container">
      <svg width="90" height="90" viewBox="0 0 90 90">
        <circle cx="45" cy="45" r={r} fill="none"
          stroke="rgba(232,236,245,0.2)" strokeWidth="7" />
        <circle cx="45" cy="45" r={r} fill="none"
          stroke={color} strokeWidth="7"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 45 45)" />
        <text x="45" y="42" textAnchor="middle"
          style={{ fill:color, fontSize:20, fontWeight:'bold', fontFamily:'var(--font-title)' }}>{score}</text>
        <text x="45" y="57" textAnchor="middle"
          style={{ fill:'#888', fontSize:9, fontFamily:'IBM Plex Sans, sans-serif' }}>/ 100</text>
      </svg>
      <div className="score-ring-label" style={{ color }}>{label} Analyst</div>
    </div>
  );
}

function HypothesisCard({ hyp, myEstimate }) {
  const days = daysUntil(hyp.resolution_date);
  const urgencyClass = days <= 7 ? 'hypothesis-urgency--critical' : days <= 30 ? 'hypothesis-urgency--high' : 'hypothesis-urgency--normal';
  const peColor = hyp.forward_pe >= 75 ? '#E74C3C' : hyp.forward_pe >= 55 ? '#F39C12' :
                  hyp.forward_pe >= 40 ? '#D4A574' : '#34495E';
  const isResolved = hyp.resolved !== null && hyp.resolved !== undefined;
  const cardClass = `hypothesis-card ${isResolved ? `hypothesis-card--resolved ${hyp.resolved === true ? 'confirmed' : hyp.resolved === false ? 'refuted' : ''}` : ''}`;

  return (
    <div className={cardClass}>
      <div className="flex gap-md">
        <div style={{ flex: 1 }}>
          {hyp.is_meta && (
            <span style={{ fontSize: '0.65rem', padding: '2px 6px', borderRadius: '3px',
              backgroundColor: 'rgba(212, 165, 116, 0.12)', color: '#D4A574',
              border: '1px solid rgba(212, 165, 116, 0.25)', fontWeight: 'bold',
              letterSpacing: '0.3px', marginRight: '8px', display: 'inline-block' }}>META</span>
          )}
          <p className="hypothesis-text">{hyp.hypothesis}</p>
          <div className="flex gap-lg flex-wrap">
            <div className="hypothesis-meta-item">
              <span className="hypothesis-date">
                {new Date(hyp.briefing_date).toLocaleDateString('en-US',
                  { month: 'short', day: 'numeric', timeZone: 'UTC' })}
              </span>
            </div>
            {!isResolved && (
              <div className="hypothesis-meta-item">
                <span className={`hypothesis-urgency ${urgencyClass}`}>
                  {days > 0 ? `${days}d remaining` : days === 0 ? 'Resolves today' : `${Math.abs(days)}d overdue`}
                </span>
              </div>
            )}
            {isResolved && (
              <div className="hypothesis-meta-item">
                <span className="hypothesis-urgency" style={{ color: hyp.resolved === true ? '#27AE60' : hyp.resolved === false ? '#E74C3C' : '#34495E' }}>
                  {hyp.resolved === true ? '✓ Confirmed' : hyp.resolved === false ? '✗ Refuted' : '~ Inconclusive'}
                </span>
              </div>
            )}
            {hyp.resolution_notes && (
              <div className="hypothesis-meta-item">
                <span style={{ color: '#888', fontSize: '0.875rem', fontStyle: 'italic' }}>
                  {hyp.resolution_notes}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="hypothesis-pe">
          <div className="hypothesis-pe-value">{hyp.forward_pe}%</div>
          <div className="hypothesis-pe-label">Platform PE</div>
        </div>
      </div>
      {myEstimate ? (
        <div className="hypothesis-estimate">
          <span style={{ color: '#D4A574' }}>YOUR ESTIMATE: {myEstimate.pe_estimate}%</span>
          <span style={{ color: '#888', marginLeft: '12px' }}>· {myEstimate.direction}</span>
          <span style={{ color: '#888', marginLeft: '12px' }}>
            · submitted {new Date(myEstimate.created_at).toLocaleDateString('en-US',
              { month: 'short', day: 'numeric', timeZone: 'UTC' })}
          </span>
          <div style={{ marginTop: '8px', color: '#888' }}>
            Δ {Math.abs(myEstimate.pe_estimate - hyp.forward_pe)}% vs platform
          </div>
        </div>
      ) : (
        <div className="hypothesis-estimate hypothesis-estimate--empty">
          No estimate submitted — open briefing to submit your PE
        </div>
      )}
    </div>
  );
}

function ArchiveTab({ archive, fetchArchive }) {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (archive.length === 0 && fetchArchive) fetchArchive();
  }, []); // eslint-disable-line

  const parse = (val) => {
    try { return typeof val === 'string' ? JSON.parse(val) : val; } catch { return val || {}; }
  };

  if (selected) {
    const b = selected;
    const keyDev = parse(b.key_developments) || [];
    const pattern = parse(b.strategic_pattern) || {};
    const sectors = parse(b.sector_watch) || {};
    const monitor = parse(b.monitoring_priorities) || [];
    return (
      <div>
        <button onClick={() => setSelected(null)} style={{
          background:'none', border:'none', color:'#E74C3C', cursor:'pointer',
          fontSize:13, marginBottom:16, padding:0 }}>← Back to Archive</button>
        <div style={{ color:'#E74C3C', fontFamily:'var(--font-title)', fontSize:20,
          fontWeight:'bold', marginBottom:4 }}>Daily Bearing</div>
        <div style={{ color:'#555', fontSize:12, marginBottom:16 }}>
          {new Date(b.date).toLocaleDateString('en-US',
            { weekday:'long', year:'numeric', month:'long', day:'numeric', timeZone:'UTC' })}
        </div>
        {b.entry_type === 'sunday' ? (
          <div style={{ color:'#888', fontSize:13 }}>Weekly Review — view in Archive tab</div>
        ) : (
          <>
            {b.executive_summary && (
              <div style={{ marginBottom:16 }}>
                <div style={{ color:'#E74C3C', fontSize:11, fontWeight:'bold',
                  letterSpacing:'0.5px', marginBottom:6 }}>EXECUTIVE SUMMARY</div>
                <p style={{ color:'#888888', fontSize:13, lineHeight:1.7, margin:0 }}>
                  {typeof b.executive_summary==='string'
                    ? b.executive_summary.replace(/^"|"$/g,'') : b.executive_summary}
                </p>
              </div>
            )}
            {Array.isArray(keyDev) && keyDev.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ color:'#E74C3C', fontSize:11, fontWeight:'bold',
                  letterSpacing:'0.5px', marginBottom:8 }}>KEY DEVELOPMENTS</div>
                {keyDev.map((dev, i) => (
                  <div key={i} style={{ padding:'10px 12px', backgroundColor:'#FFFFFF',
                    borderRadius:6, marginBottom:7, border:'1px solid rgba(52,73,94,0.1)' }}>
                    <div style={{ color:'#2C3E50', fontWeight:'bold', fontSize:13,
                      marginBottom:4 }}>{dev.headline||dev.title}</div>
                    <p style={{ color:'#777', fontSize:12, lineHeight:1.6, margin:0 }}>
                      {dev.assessment||dev.fact||''}
                    </p>
                    {dev.implications && (
                      <p style={{ color:'#555', fontSize:11, lineHeight:1.5,
                        margin:'4px 0 0', fontStyle:'italic' }}>{dev.implications}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            {pattern.observation && (
              <div style={{ marginBottom:16 }}>
                <div style={{ color:'#E74C3C', fontSize:11, fontWeight:'bold',
                  letterSpacing:'0.5px', marginBottom:6 }}>STRATEGIC PATTERN</div>
                <p style={{ color:'#888888', fontSize:13, lineHeight:1.7, margin:0 }}>
                  {pattern.observation}
                </p>
              </div>
            )}
            {Object.keys(sectors).length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ color:'#E74C3C', fontSize:11, fontWeight:'bold',
                  letterSpacing:'0.5px', marginBottom:8 }}>SECTOR WATCH</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:6 }}>
                  {Object.entries(sectors).map(([k,v]) => (
                    <div key={k} style={{ padding:'8px 10px', backgroundColor:'#FFFFFF',
                      borderRadius:6, border:'1px solid rgba(231,76,60,0.1)' }}>
                      <div style={{ color:'#E74C3C', fontSize:10, fontWeight:'bold',
                        textTransform:'uppercase', marginBottom:3 }}>{k}</div>
                      <p style={{ color:'#666', fontSize:11, lineHeight:1.5, margin:0 }}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {Array.isArray(monitor) && monitor.length > 0 && (
              <div>
                <div style={{ color:'#E74C3C', fontSize:11, fontWeight:'bold',
                  letterSpacing:'0.5px', marginBottom:8 }}>MONITOR NEXT 72 HOURS</div>
                {monitor.map((item,i) => (
                  <div key={i} style={{ display:'flex', gap:10, marginBottom:6 }}>
                    <span style={{ color:'#4a5f82', fontSize:11, flexShrink:0 }}>
                      {String(i+1).padStart(2,'0')}
                    </span>
                    <span style={{ color:'#777', fontSize:12, lineHeight:1.6 }}>{item}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // List view
  const parse2 = (val) => {
    try { return typeof val==='string' ? JSON.parse(val) : val; } catch { return val||{}; }
  };

  if (archive.length === 0) return (
    <div style={{ padding:40, textAlign:'center', color:'#555', fontSize:13 }}>
      No archived briefings yet.
    </div>
  );

  return (
    <div>
      <p style={{ color:'#555', fontSize:12, marginBottom:14 }}>
        {archive.length} briefing{archive.length!==1?'s':''} in archive
      </p>
      {archive.map((item, i) => {
        if (item.entry_type === 'sunday') {
          const execSum = parse2(item.executive_summary) || {};
          const trend = execSum.overall_threat_trend;
          const trendColor = trend==='escalating'?'#E74C3C':trend==='de-escalating'?'#27AE60':'#E74C3C';
          return (
            <div key={i} style={{ padding:'10px 14px', backgroundColor:'#FFFFFF',
              border:'1px solid rgba(231,76,60,0.25)', borderRadius:7, marginBottom:8 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <span style={{ fontSize:9, padding:'1px 6px', borderRadius:3, fontWeight:'bold',
                    backgroundColor:'rgba(231,76,60,0.12)', color:'#E74C3C',
                    border:'1px solid rgba(231,76,60,0.25)', marginRight:6 }}>SUNDAY EDITION</span>
                  <span style={{ color:'#2C3E50', fontSize:13 }}>
                    Week of {new Date(item.week_starting).toLocaleDateString('en-US',
                      { month:'short', day:'numeric' })} –{' '}
                    {new Date(item.week_ending).toLocaleDateString('en-US',
                      { month:'short', day:'numeric', year:'numeric' })}
                  </span>
                  {item.audio_url && <AudioPlayer audioUrl={item.audio_url} title="Weekly Review" />}
                </div>
                {trend && (
                  <span style={{ fontSize:10, padding:'2px 7px', borderRadius:4,
                    fontWeight:'bold', backgroundColor:trendColor+'18', color:trendColor,
                    border:`1px solid ${trendColor}44`, textTransform:'uppercase' }}>{trend}</span>
                )}
              </div>
            </div>
          );
        }
        const tl = item.threat_level;
        const tlColor = tl==='CRITICAL'?'#ff4444':tl==='HIGH'?'#ff8833':tl==='ELEVATED'?'#E74C3C':'#34495E';
        return (
          <div key={i} onClick={() => setSelected(item)}
            style={{ padding:'10px 14px', backgroundColor:'#FFFFFF',
              border:'1px solid rgba(52,73,94,0.1)', borderRadius:7, marginBottom:6,
              cursor:'pointer', transition:'border-color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor='rgba(231,76,60,0.3)'}
            onMouseLeave={e => e.currentTarget.style.borderColor='rgba(52,73,94,0.1)'}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
              <span style={{ color:'#E74C3C', fontFamily:'var(--font-title)', fontSize:13, fontWeight:'bold' }}>
                {new Date(item.date).toLocaleDateString('en-US',
                  { weekday:'short', month:'short', day:'numeric', year:'numeric', timeZone:'UTC' })}
              </span>
              {tl && (
                <span style={{ fontSize:10, padding:'1px 7px', borderRadius:3,
                  fontWeight:'bold', backgroundColor:tlColor+'18', color:tlColor,
                  border:`1px solid ${tlColor}44`, textTransform:'uppercase' }}>{tl}</span>
              )}
            </div>
            <p style={{ color:'#666', fontSize:12, lineHeight:1.5, margin:0,
              overflow:'hidden', display:'-webkit-box',
              WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
              {typeof item.executive_summary==='string'
                ? item.executive_summary.replace(/^"|"$/g,'') : item.executive_summary}
            </p>
          </div>
        );
      })}
    </div>
  );
}


export default function SubscriberDashboard({ archive = [], fetchArchive }) {
  const [score,       setScore]       = useState({ score:50, total_ratings:0, correct_calls:0, domain_profile:{} });
  const [openHyps,    setOpenHyps]    = useState([]);
  const [myEstimates, setMyEstimates] = useState([]);
  const [resolvedHyps,setResolvedHyps]= useState([]);
  const [loading,     setLoading]     = useState(true);
  const [activeTab,   setActiveTab]   = useState('score');

  const deviceUUID = getDeviceUUID();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [scoreRes, hypsRes, estimatesRes] = await Promise.all([
          fetch(`${API_URL}/api/analyst-score/${deviceUUID}`),
          fetch(`${API_URL}/api/hypotheses/open/all`),
          fetch(`${API_URL}/api/hypotheses/my-estimates/${deviceUUID}`)
        ]);
        if (scoreRes.ok) setScore(await scoreRes.json());
        if (estimatesRes.ok) { const ed = await estimatesRes.json(); setMyEstimates(ed.estimates || []); }
        if (hypsRes.ok) {
          const data = await hypsRes.json();
          const hyps = data.hypotheses || [];
          setOpenHyps(hyps.filter(h => h.resolved === null)
            .sort((a,b) => new Date(a.resolution_date) - new Date(b.resolution_date)));
          setResolvedHyps(hyps.filter(h => h.resolved !== null)
            .sort((a,b) => new Date(b.resolved_at||b.resolution_date) - new Date(a.resolved_at||a.resolution_date)));
        }
      } catch(e) {}
      finally { setLoading(false); }
    };
    load();
  }, [deviceUUID]);

  const domainProfile = score.domain_profile || {};
  const topDomains = Object.entries(domainProfile)
    .sort((a,b) => b[1]-a[1]).slice(0,5);

  const tabs = [
    { key:'score',    label:'Analyst Score' },
    { key:'open',     label:`Open (${openHyps.length})` },
    { key:'resolved', label:`Resolved (${resolvedHyps.length})` },
    { key:'archive',  label:`Archive (${archive.length})` },
  ];

  return (
    <div style={{ maxWidth:860, margin:'0 auto', padding:'24px 16px' }}>
      <div style={{ background:'rgba(245,241,232, 0.6)', border:'1px solid #c4a563', borderRadius:'8px', padding:'2rem' }}>
        <h2 style={{ color:'#E74C3C', fontFamily:'var(--font-title)', fontSize:24, marginBottom:4 }}>
          Intelligence Dashboard
        </h2>
      <p style={{ color:'#555', fontSize:12, marginBottom:20 }}>
        Your anonymous analyst profile — device-based, never linked to your identity.
      </p>

      {/* Tab nav */}
      <div style={{ display:'flex', gap:0, borderBottom:'1px solid rgba(231,76,60,0.15)', marginBottom:20 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding:'8px 16px', background:'transparent', border:'none',
            borderBottom: activeTab===t.key ? '2px solid #c4a563' : '2px solid transparent',
            color: activeTab===t.key ? '#E74C3C' : '#666',
            cursor:'pointer', fontSize:12, fontWeight:'bold',
            fontFamily:'inherit', letterSpacing:'0.3px', transition:'all 0.15s'
          }}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding:40, textAlign:'center', color:'#E74C3C' }}>Loading…</div>
      ) : (
        <>
          {/* Analyst Score tab */}
          {activeTab === 'score' && (
            <div>
              <div style={{ display:'flex', gap:24, alignItems:'flex-start',
                flexWrap:'wrap', marginBottom:24 }}>
                <ScoreRing score={score.score} />
                <div style={{ flex:1, minWidth:200 }}>
                  <div style={{ marginBottom:14 }}>
                    <div style={{ color:'#555', fontSize:10, fontWeight:'bold',
                      letterSpacing:'0.6px', marginBottom:8 }}>PERFORMANCE</div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
                      {[
                        { label:'Total ratings', value: score.total_ratings },
                        { label:'Correct calls', value: score.correct_calls },
                        { label:'Accuracy', value: score.total_ratings > 0
                          ? `${Math.round(score.correct_calls/score.total_ratings*100)}%` : '—' }
                      ].map(s => (
                        <div key={s.label} style={{ padding:'8px', backgroundColor:'#FFFFFF',
                          borderRadius:6, border:'1px solid rgba(52,73,94,0.1)', textAlign:'center' }}>
                          <div style={{ color:'#E74C3C', fontSize:16, fontWeight:'bold' }}>{s.value}</div>
                          <div style={{ color:'#555', fontSize:10 }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {topDomains.length > 0 && (
                    <div>
                      <div style={{ color:'#555', fontSize:10, fontWeight:'bold',
                        letterSpacing:'0.6px', marginBottom:8 }}>DOMAIN EXPERTISE</div>
                      {topDomains.map(([domain, count]) => (
                        <div key={domain} style={{ display:'flex', alignItems:'center',
                          gap:8, marginBottom:5 }}>
                          <span style={{ color:'#888', fontSize:11, minWidth:80 }}>{domain}</span>
                          <div style={{ flex:1, height:4, backgroundColor:'rgba(52,73,94,0.1)',
                            borderRadius:2 }}>
                            <div style={{ height:'100%', borderRadius:2,
                              backgroundColor:'#E74C3C',
                              width:`${Math.min(100,(count/Math.max(...Object.values(domainProfile)))*100)}%` }} />
                          </div>
                          <span style={{ color:'#555', fontSize:10 }}>{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* How scoring works */}
              <div style={{ padding:'12px 14px', backgroundColor:'rgba(231,76,60,0.04)',
                borderRadius:6, border:'1px solid rgba(231,76,60,0.1)' }}>
                <div style={{ color:'#E74C3C', fontSize:10, fontWeight:'bold',
                  letterSpacing:'0.5px', marginBottom:8 }}>HOW ANALYST SCORE WORKS</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',
                  gap:6, fontSize:11, color:'#555', lineHeight:1.6 }}>
                  <div>+3 pts: Rated signal 4-5 on a confirmed hypothesis</div>
                  <div>+3 pts: Rated signal 1-2 on a refuted hypothesis</div>
                  <div>-2 pts: Rated signal 4-5 on a refuted hypothesis</div>
                  <div>-2 pts: Rated signal 1-2 on a confirmed hypothesis</div>
                  <div>+1 pt: Submitted PE estimate within 2hrs of publish</div>
                  <div>Scores update when admin resolves each hypothesis</div>
                </div>
              </div>
            </div>
          )}

          {/* Open hypotheses tab */}
          {activeTab === 'open' && (
            <div>
              {openHyps.length === 0 ? (
                <div style={{ padding:40, textAlign:'center', color:'#555', fontSize:13 }}>
                  <div style={{ fontSize:28, marginBottom:8 }}>📋</div>
                  No open hypotheses yet. Tomorrow's briefing will generate the first batch.
                </div>
              ) : (
                <>
                  <p style={{ color:'#555', fontSize:12, marginBottom:14 }}>
                    {openHyps.length} open hypothesis{openHyps.length!==1?'es':''} tracking across active briefings.
                    Submit your PE estimates to build your Analyst Score.
                  </p>
                  {openHyps.map(h => <HypothesisCard key={h.id} hyp={h} myEstimate={myEstimates.find(e => e.hypothesis_id === h.id)} />)}
                </>
              )}
            </div>
          )}

          {/* Archive tab */}
          {activeTab === 'archive' && (
            <ArchiveTab archive={archive} fetchArchive={fetchArchive} />
          )}

          {/* Archive tab */}
          {activeTab === 'archive' && (
            <ArchiveTab archive={archive} fetchArchive={fetchArchive} />
          )}

          {/* Resolved tab */}
          {activeTab === 'resolved' && (
            <div>
              {resolvedHyps.length === 0 ? (
                <div style={{ padding:40, textAlign:'center', color:'#555', fontSize:13 }}>
                  No resolved hypotheses yet.
                </div>
              ) : (
                <>
                  <p style={{ color:'#555', fontSize:12, marginBottom:14 }}>
                    {resolvedHyps.filter(h=>h.resolved===true).length} confirmed ·{' '}
                    {resolvedHyps.filter(h=>h.resolved===false).length} refuted ·{' '}
                    {resolvedHyps.filter(h=>h.resolved===null||h.resolved===undefined).length} inconclusive
                  </p>
                  {resolvedHyps.map(h => <HypothesisCard key={h.id} hyp={h} myEstimate={myEstimates.find(e => e.hypothesis_id === h.id)} />)}
                </>
              )}
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}
